import { AfterViewInit, Component, computed, inject, NgZone, OnDestroy, signal } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import NutrientViewer from '@nutrient-sdk/viewer';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';

import { Router } from '@angular/router';
import { RouteCodeEnum } from '@models/menu';
import { AttachmentModel } from '@models/module/fields/main';
import { Permission } from '@models/permission';
import {
  DocumentLinkCustomData,
  Nutrient,
  NutrientAnnotationTooltipItem,
  NutrientConnectedClients,
  NutrientPagePressEvent,
  NutrientService,
  NutrientToolbarItem,
} from '@module-definition/nutrient/common';
import {
  NutrientDocumentLinkDialogComponent,
  NutrientDocumentLinkDialogData,
} from '@module-definition/nutrient/nutrient-document-link-dialog/nutrient-document-link-dialog.component';
import { MyDriveService } from '@module-instance/my-drive/common';
import {
  PdfMarkupAssociationDetail,
  PdfMarkupService,
  PdfMarkupUser,
  PdfMarkupVersion,
} from '@module-instance/pdf-markups/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CipoImgBase64 } from '@shared/components/img-base64/img-base64.component';
import { DIALOG_SIZE } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules';
import { ItvDateTimePipe } from '@shared/pipes/itv-date.pipe';
import { NotificationService } from '@shared/services';
import { UserService } from '@shared/services/user.service';
import { UserStore } from '@signal-store';

// 792 points is the height of a US Letter page at 72 points per inch.
const DOCUMENT_LINK_REFERENCE_PAGE_HEIGHT = 792;

export type NutrientDialogData = {
  documents: Pick<AttachmentModel, 'versionId' | 'mimeType' | 'thumbnail50' | 'thumbnail200' | 'name'>[];
  startIndex?: number;
  isMarkup?: boolean;
  documentId?: string;
  entityInstanceId?: number;
  fileId?: number;
  /** Open this associated version instead of the latest markup version. */
  fileVersionId?: number;
  /** Scroll to the documentLink annotation for this dynamic document. */
  moduleTypeInstanceId?: number;
  /** Module whose permissions govern annotation editing. Defaults to PDF Markup when omitted. */
  moduleId?: number;
};

@Component({
  selector: 'cipo-nutrient',
  imports: [
    CipoDialogModule,
    CipoDialogHeaderComponent,
    CipoImgBase64,
    ItvDateTimePipe,
    MatBadgeModule,
    MatMenuModule,
    TranslateModule,
  ],
  templateUrl: './nutrient.component.html',
  styleUrls: ['./nutrient.component.scss'],
  providers: [PdfMarkupService],
})
export class NutrientComponent implements AfterViewInit, OnDestroy {
  private nutrientService = inject(NutrientService);
  private folderFileService = inject(MyDriveService);
  private pdfMarkupService = inject(PdfMarkupService);
  private userStore = inject(UserStore);
  private userService = inject(UserService);
  private notify = inject(NotificationService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<NutrientComponent>);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private translate = inject(TranslateService);
  data = inject<NutrientDialogData>(MAT_DIALOG_DATA);

  /* Dictionary to store version URLs */
  filesDict = signal<Record<number, string>>({});
  selectedFileIndex = signal(this.data.startIndex ?? 0);
  selectedFile = () => this.data.documents[this.selectedFileIndex()];
  connectedUsers = signal<PdfMarkupUser[]>([]);
  showClickMessage = signal(false);
  versions = signal<PdfMarkupVersion[]>([]);
  selectedVersion = signal<PdfMarkupVersion>(null);
  isVersionsBusy = signal(false);
  currentDocumentId = signal<string>(this.data.documentId);
  viewerLoadError = signal<string>(null);

  isDirty = false;

  private nutrientInstance: Nutrient.Instance = null;
  /* Only the latest version uploaded to PSPDFKit is editable; older versions are read-only. */
  private latestMarkupDocumentId: string = null;
  private associationDetails = new Map<number, PdfMarkupAssociationDetail>();
  private userDict: PdfMarkupUser[] = [];
  private mentionObserver: MutationObserver = null;
  private attachedTextareas = new WeakSet<HTMLTextAreaElement>();
  // Programmatic deletes already update the backend; their SDK event must not issue a duplicate request.
  private suppressedAnnotationDeleteIds = new Set<string>();
  private canUpdateAnnotations = computed(() =>
    this.userStore.getModuleByCode(RouteCodeEnum.PDFMarkup)?.operations?.some(op => op.id === Permission.Update),
  );
  private canDeleteDocumentLinks = computed(() => {
    const contract = this.userStore.contract();
    if (contract?.isClosed || contract?.isArchived) return false;
    return !!this.userStore
      .getModuleByCode(RouteCodeEnum.PDFMarkup)
      ?.operations?.some(op => op.id === Permission.Delete);
  });
  private mentionListSequence = 0;

  ngAfterViewInit() {
    if (this.data.isMarkup) {
      if (this.data.fileId) {
        /* Load versions first so we open with the correct editable/read-only state. */
        this.loadVersions(this.data.fileId, () => {
          const sv = this.selectedVersion();
          if (sv?.documentId) {
            this.loadMarkupDocument(sv.documentId, sv.documentId === this.latestMarkupDocumentId);
          } else {
            // Versions without a PSPDFKit document open from their file URL read-only.
            const fileUrl = sv?.fileUrl ?? null;
            if (fileUrl) {
              this.loadViewer(this.nutrientService.nutrientViewConfig(fileUrl));
            } else {
              this.handleViewerLoadError();
            }
          }
        });
      } else {
        this.loadMarkupDocument();
      }
    } else {
      if (this.data.fileId) {
        this.loadVersions(this.data.fileId);
      }
      /* Load version URLs for each document */
      this.folderFileService.getFilesUrl(this.data.documents.map(doc => doc.versionId)).subscribe({
        next: urls => {
          const dict: Record<number, string> = {};
          urls.forEach(item => {
            dict[item.versionId] = item.url;
          });
          this.filesDict.set(dict);
          this.loadDocument(this.data.documents[this.selectedFileIndex()].versionId);
        },
        /* Without a URL there is nothing to render, so close instead of leaving an empty viewer. */
        error: () => this.dialogRef.close(),
      });
    }
  }

  ngOnDestroy() {
    if (this.nutrientInstance && this.data.isMarkup) {
      this.nutrientInstance.removeEventListener('annotations.create', this.onAnnotationsChange);
      this.nutrientInstance.removeEventListener('annotations.update', this.onAnnotationsChange);
      this.nutrientInstance.removeEventListener('annotations.delete', this.onAnnotationsDelete);
      if (this.selectedVersion()?.documentId) {
        this.nutrientInstance.removeEventListener('instant.connectedClients.change', this.onConnectedClientsChange);
      }
    }
    this.mentionObserver?.disconnect();
    this.mentionObserver = null;
    NutrientViewer.unload('#nutrient-container');
    this.nutrientInstance = null;
  }

  selectFile(direction: 'prev' | 'next') {
    const currentIndex = this.selectedFileIndex() + (direction === 'prev' ? -1 : 1);

    this.selectedFileIndex.set(currentIndex);
    this.loadDocument(this.data.documents[currentIndex].versionId, currentIndex);
  }

  async loadDocument(versionId: number, index?: number) {
    NutrientViewer.unload('#nutrient-container');
    this.nutrientInstance = null;
    const currentDocUrl = this.filesDict()[versionId];
    this.selectedFileIndex.update(oldIndex => index ?? oldIndex);
    this.loadViewer(this.nutrientService.nutrientViewConfig(currentDocUrl));
  }

  private loadMarkupDocument(documentId = this.data.documentId, isCurrent = true) {
    this.currentDocumentId.set(documentId);
    forkJoin({
      auth: this.pdfMarkupService.getToken(documentId, this.data.entityInstanceId, this.data.moduleId),
      associations: this.pdfMarkupService
        .getAssociationDetails(documentId, this.data.entityInstanceId)
        .pipe(catchError(() => of([]))),
      users: this.pdfMarkupService
        .getUserDict(this.userStore.contract()?.entityInstanceId ?? this.data.entityInstanceId)
        .pipe(catchError(() => of([]))),
    }).subscribe(({ auth, associations, users }) => {
      associations.forEach(a => this.associationDetails.set(a.moduleTypeInstanceId, a));
      this.userDict = users;
      const currentUserId = this.userStore.userData()?.id?.toString() ?? '';
      this.nutrientService
        .nutrientMarkupConfig(documentId, auth.jwt, {
          showDocumentLinkDialog: this.showDocumentLinkDialog,
          placeIcon: this.placeIcon,
          annotationTooltipCallback: this.buildAnnotationTooltip,
          permissions: auth.permissions,
          canUpdateAnnotations: this.canUpdateAnnotations(),
          isCurrent,
        })
        .then(config =>
          this.loadViewer(config, instance => {
            instance.setAnnotationCreatorName(currentUserId);
            if (isCurrent) {
              this.updateConnectedUsers(instance.connectedClients);
              instance.addEventListener('instant.connectedClients.change', this.onConnectedClientsChange);
            }
            this.setToolbarItemOnPress(instance);
            instance.addEventListener('annotations.create', this.onAnnotationsChange);
            instance.addEventListener('annotations.update', this.onAnnotationsChange);
            instance.addEventListener('annotations.delete', this.onAnnotationsDelete);
            this.setupMentionObserver();
            void this.scrollToDocumentLink(instance);
          }),
        )
        .catch(() => this.handleViewerLoadError());
    });
  }

  private loadVersions(fileId: number, onComplete?: () => void) {
    this.isVersionsBusy.set(true);
    this.pdfMarkupService
      .getVersions(this.data.entityInstanceId, fileId, !!this.data.isMarkup)
      .pipe(catchError(() => of([])))
      .subscribe(versions => {
        this.versions.set(versions);
        /* A version without a documentId was never uploaded to PSPDFKit → view-only.
           The latest uploaded version is the one the user can edit. */
        const versionsWithDocId = versions.filter(v => !!v.documentId);
        const latestMarkupVersion = versionsWithDocId.length
          ? versionsWithDocId.reduce((a, b) => (b.version > a.version ? b : a))
          : null;
        this.latestMarkupDocumentId = latestMarkupVersion?.documentId ?? null;
        /* References open their associated version; other callers keep their existing defaults. */
        this.selectedVersion.set(
          this.data.fileVersionId != null
            ? (versions.find(v => v.fileVersionId === this.data.fileVersionId) ?? null)
            : this.data.isMarkup
              ? (latestMarkupVersion ?? versions.find(v => v.isCurrent) ?? versions[0] ?? null)
              : (versions.find(v => v.isCurrent) ?? versions[0] ?? null),
        );
        this.isVersionsBusy.set(false);
        onComplete?.();
      });
  }

  private async scrollToDocumentLink(instance: Nutrient.Instance): Promise<void> {
    const moduleTypeInstanceId = this.data.moduleTypeInstanceId;
    if (moduleTypeInstanceId == null) return;

    try {
      // Page order makes the first reference deterministic if a document has several matching annotations.
      for (let pageIndex = 0; pageIndex < instance.totalPageCount; pageIndex++) {
        if (this.nutrientInstance !== instance) return;
        const annotations = await instance.getAnnotations(pageIndex);
        if (this.nutrientInstance !== instance) return;
        const annotation = annotations.find(a => {
          const customData = a.customData as DocumentLinkCustomData;
          return (
            customData?.annotationType === 'documentLink' && customData.moduleTypeInstanceId === moduleTypeInstanceId
          );
        });
        if (annotation) {
          instance.jumpToRect(annotation.pageIndex, annotation.boundingBox);
          return;
        }
      }
    } catch {
      // Missing/unavailable annotations must not prevent reviewing the PDF itself.
    }
  }

  changeVersion = (version: PdfMarkupVersion) => {
    const previousHadInstant = !!this.selectedVersion()?.documentId;
    this.selectedVersion.set(version);
    if (this.nutrientInstance) {
      this.nutrientInstance.removeEventListener('annotations.delete', this.onAnnotationsDelete);
      if (previousHadInstant) {
        this.nutrientInstance.removeEventListener('instant.connectedClients.change', this.onConnectedClientsChange);
      }
    }
    NutrientViewer.unload('#nutrient-container');
    this.nutrientInstance = null;
    if (version.documentId) {
      const isLatestMarkup = version.documentId === this.latestMarkupDocumentId;
      this.loadMarkupDocument(version.documentId, isLatestMarkup);
    } else {
      this.loadViewer(this.nutrientService.nutrientViewConfig(version.fileUrl));
    }
  };

  private loadViewer(
    configOrPromise: Nutrient.Configuration | Promise<Nutrient.Configuration>,
    onLoaded?: (instance: Nutrient.Instance) => void,
  ): void {
    this.viewerLoadError.set(null);
    Promise.resolve(configOrPromise)
      .then(config => this.ngZone.runOutsideAngular(() => NutrientViewer.load(config)))
      .then(instance =>
        this.ngZone.run(() => {
          this.nutrientInstance = instance;
          onLoaded?.(instance);
        }),
      )
      .catch(() => this.handleViewerLoadError());
  }

  private handleViewerLoadError(): void {
    this.ngZone.run(() => {
      if (document.querySelector('#nutrient-container')) {
        NutrientViewer.unload('#nutrient-container');
      }
      this.nutrientInstance = null;
      this.viewerLoadError.set('pdfMarkups.documentUnavailable');
    });
  }

  private setToolbarItemOnPress(instance: Nutrient.Instance) {
    instance.setToolbarItems(items =>
      items.map(item => {
        if (item.type !== 'custom') (item as NutrientToolbarItem).onPress = this.cancelPlaceIcon;
        return item;
      }),
    );
  }

  private onAnnotationsChange = () => {
    this.isDirty = true;
  };

  private onConnectedClientsChange = (clients: NutrientConnectedClients) => {
    this.updateConnectedUsers(clients);
  };

  private updateConnectedUsers(clients: NutrientConnectedClients) {
    const currentUserId = this.userStore.userData()?.id?.toString();
    const connectedUserIds = clients
      .valueSeq()
      .map(c => c.userId)
      .filter(id => !!id && id !== currentUserId)
      .toSet()
      .toArray();
    const connected = this.userDict.filter(u => connectedUserIds.includes(u.id.toString()));
    this.ngZone.run(() => this.connectedUsers.set(connected));
  }

  private onAnnotationsDelete = (deletedAnnotations: Nutrient.List<Nutrient.AnnotationsUnion>) => {
    this.isDirty = true;
    const annotation = deletedAnnotations.get(0);
    const customData = annotation?.customData as DocumentLinkCustomData;
    const documentId = this.currentDocumentId();
    const instance = this.nutrientInstance;
    if (customData?.annotationType === 'documentLink') {
      if (this.suppressedAnnotationDeleteIds.delete(annotation.id)) return;

      this.ngZone.run(() => {
        void firstValueFrom(
          this.pdfMarkupService.deleteDocumentLink(
            documentId,
            customData.moduleTypeInstanceId,
            this.data.entityInstanceId,
          ),
        )
          .then(() => this.associationDetails.delete(customData.moduleTypeInstanceId))
          .catch(async () => {
            try {
              await instance?.delete(annotation);
            } finally {
              this.notify.error('pdfMarkups.docLink.deleteError');
            }
          });
      });
    }
  };

  private setupMentionObserver() {
    const shadowHost = document.querySelector('#nutrient-container .PSPDFKit-Container');
    const shadowRoot = (shadowHost as Element & { shadowRoot: ShadowRoot })?.shadowRoot;
    if (!shadowRoot) return;

    this.injectShadowStyles(shadowRoot);

    this.mentionObserver = new MutationObserver(() => {
      const inputEl = shadowRoot.querySelector<HTMLTextAreaElement>('textarea[data-testid="note-annotation-textarea"]');
      if (!inputEl || this.attachedTextareas.has(inputEl)) return;
      this.attachedTextareas.add(inputEl);
      // PSPDFKit renders the note-annotation textarea without a label — add one (WCAG 4.1.2 / 1.3.1).
      if (!inputEl.getAttribute('aria-label')) {
        inputEl.setAttribute('aria-label', this.translate.instant('a11y.pdfMarkups.noteAnnotation'));
      }
      this.attachMentionListener(inputEl, shadowRoot);
    });

    this.mentionObserver.observe(shadowRoot, { childList: true, subtree: true });
  }

  private injectShadowStyles(shadowRoot: ShadowRoot) {
    const style = document.createElement('style');
    style.textContent = `
      /* @-mention dropdown */
      .mention-options { box-sizing: border-box; position: absolute; width: 100%; opacity: 0; pointer-events: none; background: #fff; color: #333; display: flex; flex-wrap: wrap; z-index: 9; }
      .mention-option { box-sizing: border-box; background: #fff; position: relative; cursor: pointer; display: block; width: 100%; border: thin solid #dddddd; padding: 7px 10px; }
      .mention-option:hover, .mention-option[aria-selected="true"] { background: #3366ff; color: #fff; }
      .show { opacity: 1; pointer-events: initial; }

      /* Document-association tooltip (structure only; the rest is Nutrient's own chrome) */
      .PSPDFKit-Popover-Annotation-Tooltip .PSPDFKit-Popover { background-color: transparent; }
      .PSPDFKit-Popover-Annotation-Tooltip .annotation-tooltip-container { background-color: transparent; border: none; }
      .annotation-inspector { font-size: 0.75rem; background-color: #fcfdfe; border-radius: 5px; min-width: 300px; padding-bottom: 1rem; pointer-events: all; user-select: text; }
      .annotation-inspector header { padding: 0.25rem 0; background-color: rgba(216, 223, 235, 0.5); border-radius: 5px 5px 0 0; }
      .annotation-inspector h1 { color: #848c9a; font-size: 0.75rem; font-weight: normal; }
      .annotation-inspector section { display: flex; flex-direction: column; background-color: rgba(240, 243, 249, 0.5); }
      .annotation-inspector .section-title { align-self: flex-end; margin: 6px 0.4rem 6px 0; }
      .annotation-inspector .group-title { color: #2b2e36; font-weight: bold; display: flex; align-items: center; cursor: pointer; }
      .annotation-inspector ul { margin: 0; padding: 0; list-style-type: none; overflow: auto; }
      .annotation-inspector li { display: flex; align-items: center; text-align: left; color: #555; background-color: #fcfdfe; box-shadow: inset 0 -0.5px 0 #e5e9f1; border-bottom: solid 0.25px rgba(216, 223, 235, 0.5); padding: 0.375rem 0.75rem; }
      .annotation-inspector li span { flex: 1; }
      .annotation-inspector li span.attr { color: #3d424e; display: flex; align-items: center; margin-right: 10px; }
      .annotation-inspector .val { color: #2b2e36; text-align: right; display: flex; align-items: center; justify-content: flex-end; height: 28px; }
      .annotation-inspector .val > * { font-size: 0.75rem; color: #2b2e36; }
      .collapse-toggle-button { border: none; background: none; display: block; padding: 0; margin: 0; cursor: pointer; height: 24px; width: 24px; }
    `;
    shadowRoot.appendChild(style);
  }

  private attachMentionListener(inputEl: HTMLTextAreaElement, shadowRoot: ShadowRoot) {
    const ownerDocument = shadowRoot.ownerDocument;
    const dropdown = ownerDocument.createElement('div');
    const dropdownId = `nutrient-mention-options-${++this.mentionListSequence}`;
    dropdown.id = dropdownId;
    dropdown.className = 'mention-options';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', this.translate.instant('a11y.pdfMarkups.mentionSuggestions'));
    inputEl.parentElement?.appendChild(dropdown);
    inputEl.setAttribute('role', 'combobox');
    inputEl.setAttribute('aria-autocomplete', 'list');
    inputEl.setAttribute('aria-haspopup', 'listbox');
    inputEl.setAttribute('aria-controls', dropdownId);
    inputEl.setAttribute('aria-expanded', 'false');

    let activeIndex = -1;
    let rafId = 0;
    const stopTracking = () => cancelAnimationFrame(rafId);

    const closeDropdown = () => {
      dropdown.classList.remove('show');
      dropdown.innerHTML = '';
      activeIndex = -1;
      inputEl.setAttribute('aria-expanded', 'false');
      inputEl.removeAttribute('aria-activedescendant');
      stopTracking();
    };

    const setActiveIndex = (index: number) => {
      const options = Array.from(dropdown.querySelectorAll<HTMLElement>('[role="option"]'));
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) =>
        option.setAttribute('aria-selected', optionIndex === activeIndex ? 'true' : 'false'),
      );
      inputEl.setAttribute('aria-activedescendant', options[activeIndex].id);
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    const selectMention = (index: number) => {
      const user = this.userDict[index];
      if (!user) return;
      const atPos = inputEl.value.lastIndexOf('@');
      inputEl.focus();
      inputEl.setSelectionRange(atPos + 1, inputEl.value.length);
      ownerDocument.execCommand('insertText', false, user.name + ' ');
      closeDropdown();
    };

    /* ── Block pointerdown at document capture phase ──
       PSPDFKit / React Aria uses pointerdown to detect clicks outside
       the note popover. When the mention dropdown extends visually
       outside the popover, PSPDFKit sees the click as "outside" and
       closes the note. By intercepting pointerdown at the document
       capture phase (coordinate-based), we prevent it from reaching
       PSPDFKit's handler. preventDefault() also suppresses the
       subsequent mousedown and prevents the textarea from losing focus. */
    const capturePointerDown = (e: PointerEvent) => {
      if (!dropdown.classList.contains('show')) return;
      const rect = dropdown.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    ownerDocument.addEventListener('pointerdown', capturePointerDown, true);

    const getCaretRect = (): DOMRect => {
      const cursorPos = inputEl.selectionEnd ?? inputEl.value.length;
      const s = window.getComputedStyle(inputEl);
      const r = inputEl.getBoundingClientRect();
      const mirror = ownerDocument.createElement('div');
      mirror.style.position = 'fixed';
      mirror.style.top = `${r.top - inputEl.scrollTop}px`;
      mirror.style.left = `${r.left}px`;
      mirror.style.width = `${r.width}px`;
      mirror.style.fontFamily = s.fontFamily;
      mirror.style.fontSize = s.fontSize;
      mirror.style.fontWeight = s.fontWeight;
      mirror.style.fontStyle = s.fontStyle;
      mirror.style.lineHeight = s.lineHeight;
      mirror.style.letterSpacing = s.letterSpacing;
      mirror.style.paddingTop = s.paddingTop;
      mirror.style.paddingRight = s.paddingRight;
      mirror.style.paddingBottom = s.paddingBottom;
      mirror.style.paddingLeft = s.paddingLeft;
      mirror.style.borderTopWidth = s.borderTopWidth;
      mirror.style.borderRightWidth = s.borderRightWidth;
      mirror.style.borderBottomWidth = s.borderBottomWidth;
      mirror.style.borderLeftWidth = s.borderLeftWidth;
      mirror.style.borderStyle = 'solid';
      mirror.style.boxSizing = s.boxSizing;
      mirror.style.whiteSpace = 'pre-wrap';
      mirror.style.wordBreak = 'break-word';
      mirror.style.overflow = 'hidden';
      mirror.style.visibility = 'hidden';
      mirror.style.pointerEvents = 'none';
      mirror.textContent = inputEl.value.substring(0, cursorPos);
      const marker = ownerDocument.createElement('span');
      marker.textContent = '\u200b';
      mirror.appendChild(marker);
      ownerDocument.body.appendChild(mirror);
      const markerRect = marker.getBoundingClientRect();
      ownerDocument.body.removeChild(mirror);
      return markerRect;
    };

    const positionDropdown = () => {
      const caret = getCaretRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const maxH = 200;
      const dropW = Math.min(inputEl.getBoundingClientRect().width, 280);
      dropdown.style.position = 'fixed';
      dropdown.style.width = `${dropW}px`;
      dropdown.style.maxHeight = `${maxH}px`;
      dropdown.style.overflowY = 'auto';
      dropdown.style.zIndex = '99999';
      dropdown.style.bottom = '';
      const left = Math.min(caret.left, window.innerWidth - dropW - 8);
      dropdown.style.left = `${Math.max(0, left)}px`;
      if (viewportHeight - caret.bottom >= maxH) {
        dropdown.style.top = `${caret.bottom}px`;
      } else if (caret.top >= maxH) {
        dropdown.style.top = `${caret.top - maxH}px`;
      } else {
        dropdown.style.top = `${caret.bottom}px`;
      }
    };

    const startTracking = () => {
      const tick = () => {
        if (!dropdown.classList.contains('show')) return;
        positionDropdown();
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    inputEl.addEventListener('input', () => {
      const text = inputEl.value ?? '';
      if (!text.replace(/\s/g, '').endsWith('@')) {
        closeDropdown();
        return;
      }

      dropdown.innerHTML = '';
      activeIndex = -1;
      this.userDict.forEach((user, index) => {
        const option = ownerDocument.createElement('div');
        option.id = `${dropdownId}-option-${index}`;
        option.className = 'mention-option';
        option.textContent = user.name;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        /* Use pointerup for selection — pointerdown/mousedown are blocked
           at the document capture level to prevent PSPDFKit from closing
           the note, so they never reach the option. pointerup flows
           through naturally. */
        option.addEventListener('pointerup', () => selectMention(index));
        dropdown.appendChild(option);
      });
      positionDropdown();
      dropdown.classList.add('show');
      inputEl.setAttribute('aria-expanded', 'true');
      startTracking();
    });

    inputEl.addEventListener('keydown', event => {
      if (!dropdown.classList.contains('show')) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex(activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex(activeIndex <= 0 ? this.userDict.length - 1 : activeIndex - 1);
          break;
        case 'Enter':
        case ' ':
          if (activeIndex < 0) return;
          event.preventDefault();
          selectMention(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          closeDropdown();
          break;
      }
    });

    inputEl.addEventListener('blur', closeDropdown);

    /* Clean up document-level listener when the textarea is removed */
    const cleanupObserver = new MutationObserver(() => {
      if (!inputEl.isConnected) {
        ownerDocument.removeEventListener('pointerdown', capturePointerDown, true);
        cleanupObserver.disconnect();
      }
    });
    cleanupObserver.observe(inputEl.parentElement ?? shadowRoot, { childList: true, subtree: true });
  }

  private buildAnnotationTooltip = (annotation: Nutrient.AnnotationsUnion): NutrientAnnotationTooltipItem[] => {
    // documentLink custom annotation — show association details
    if (
      annotation instanceof NutrientViewer.Annotations.ImageAnnotation &&
      (annotation.customData as DocumentLinkCustomData)?.annotationType === 'documentLink'
    ) {
      const customData = annotation.customData as DocumentLinkCustomData;
      const container = document.createElement('div');
      container.className = 'annotation-inspector';
      const header = document.createElement('header');
      const heading = document.createElement('h1');
      heading.textContent = this.translate.instant('pdfMarkups.docLink.title');
      header.appendChild(heading);
      container.appendChild(header);
      const cached = this.associationDetails.get(customData.moduleTypeInstanceId);
      if (cached) container.appendChild(this.buildDetailSection(cached));
      return [{ type: 'custom', id: 'annotation-tooltip', className: 'annotation-tooltip-container', node: container }];
    }

    // Standard annotations — use Nutrient's native annotation toolbar
    return [];
  };

  private buildDetailSection(details: PdfMarkupAssociationDetail): HTMLElement {
    const tenantId = this.userStore.userData()?.tenantIdentifier?.toLowerCase() ?? '';
    const contractId = this.userStore.contract()?.id ?? 0;
    const url =
      `/${tenantId}/dm/${contractId}/${details.moduleId}/details/0/${details.entityId}/${details.screenId}`.toLowerCase();
    let displayName = details.name;
    if (displayName.startsWith('Report-')) {
      const commaIdx = displayName.indexOf(',');
      if (commaIdx > -1) {
        displayName =
          displayName.substring(0, commaIdx + 1) +
          ' ' +
          new Date(displayName.substring(commaIdx + 1).trim()).toLocaleDateString();
      }
    }

    const section = document.createElement('section');
    const titleDiv = document.createElement('div');
    titleDiv.className = 'section-title';
    const groupTitle = document.createElement('span');
    groupTitle.className = 'group-title';
    groupTitle.textContent = 'Details';
    const btn = document.createElement('button');
    btn.className = 'collapse-toggle-button';
    btn.type = 'button';
    btn.setAttribute('aria-label', this.translate.instant('actions.collapse'));
    const icon = document.createElement('img');
    icon.src = 'Content/pspdfkit/icons/panel-collapse.svg';
    icon.alt = '';
    btn.appendChild(icon);
    groupTitle.appendChild(btn);
    titleDiv.appendChild(groupTitle);
    section.appendChild(titleDiv);

    const ul = document.createElement('ul');
    ul.id = 'ul-details';

    btn.addEventListener('click', () => {
      const collapsed = ul.style.display === 'none';
      ul.style.display = collapsed ? '' : 'none';
      btn.setAttribute('aria-label', this.translate.instant(collapsed ? 'actions.collapse' : 'actions.expand'));
    });

    const nameLink = document.createElement('a');
    nameLink.href = url;
    nameLink.textContent = displayName;
    nameLink.addEventListener('click', event => {
      if (event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      this.ngZone.run(() => {
        const normalizePath = (u: string) => this.router.serializeUrl(this.router.parseUrl(u)).split('?')[0];
        if (normalizePath(this.router.url) === normalizePath(url)) {
          // Already viewing this document — just close the Nutrient viewer
          this.dialogRef.close();
        } else {
          this.dialog.openDialogs.forEach(dialogRef => dialogRef.close({ skipAfterClose: true }));
          this.router.navigateByUrl(url);
        }
      });
    });
    [
      { key: this.translate.instant('pdfMarkups.docTable.name'), value: nameLink },
      { key: this.translate.instant('pdfMarkups.docTable.createdBy'), value: details.createdBy },
      {
        key: this.translate.instant('pdfMarkups.docTable.createdOn'),
        value: new Date(details.createdOn).toLocaleDateString(),
      },
    ].forEach(({ key, value }) => {
      const li = document.createElement('li');
      const keySpan = document.createElement('span');
      keySpan.className = 'attr';
      keySpan.textContent = key;
      const valueContainer = document.createElement('span');
      valueContainer.className = 'val';
      const valueSpan = document.createElement('span');
      if (value instanceof Node) {
        valueSpan.appendChild(value);
      } else {
        valueSpan.textContent = value;
      }
      valueContainer.appendChild(valueSpan);
      li.append(keySpan, valueContainer);
      ul.appendChild(li);
    });
    section.appendChild(ul);
    return section;
  }

  showDocumentLinkDialog = () => {
    this.ngZone.run(() =>
      this.dialog.open<NutrientDocumentLinkDialogComponent, NutrientDocumentLinkDialogData>(
        NutrientDocumentLinkDialogComponent,
        {
          panelClass: ['cipo-dialog', 'classic'],
          ...(this.userStore.isSmallScreen() ? DIALOG_SIZE.full : this.userService.getResponsiveDialogSize().md),
          data: {
            documentId: this.currentDocumentId(),
            entityInstanceId: this.data.entityInstanceId,
            deleteAnnotation: this.deleteAnnotationFromViewer,
            canDelete: this.canDeleteDocumentLinks(),
          },
        },
      ),
    );
  };

  deleteAnnotationFromViewer = async (moduleTypeInstanceId: number): Promise<void> => {
    if (!this.nutrientInstance) throw new Error('Nutrient viewer is not loaded');
    const instance = this.nutrientInstance;
    let matchingAnnotation: Nutrient.AnnotationsUnion = null;
    for (let pageIndex = 0; pageIndex < instance.totalPageCount; pageIndex++) {
      const annotations = await instance.getAnnotations(pageIndex);
      matchingAnnotation = annotations.find(
        a =>
          (a.customData as DocumentLinkCustomData)?.moduleTypeInstanceId === moduleTypeInstanceId &&
          (a.customData as DocumentLinkCustomData)?.annotationType === 'documentLink',
      );
      if (matchingAnnotation) break;
    }
    const documentId = this.currentDocumentId();

    await firstValueFrom(
      this.pdfMarkupService.deleteDocumentLink(documentId, moduleTypeInstanceId, this.data.entityInstanceId),
    ).then(() => this.associationDetails.delete(moduleTypeInstanceId));

    if (matchingAnnotation) {
      // Backend-first keeps the visible row/annotation intact when authorization or the network rejects deletion.
      this.suppressedAnnotationDeleteIds.add(matchingAnnotation.id);
      try {
        await instance.delete(matchingAnnotation);
      } catch (error) {
        this.suppressedAnnotationDeleteIds.delete(matchingAnnotation.id);
        throw error;
      }
    }
  };

  deleteAnnotationOnlyFromViewer = async (moduleTypeInstanceId: number): Promise<void> => {
    if (!this.nutrientInstance) throw new Error('Nutrient viewer is not loaded');
    const instance = this.nutrientInstance;
    for (let pageIndex = 0; pageIndex < instance.totalPageCount; pageIndex++) {
      const annotations = await instance.getAnnotations(pageIndex);
      const annotation = annotations.find(
        a =>
          (a.customData as DocumentLinkCustomData)?.moduleTypeInstanceId === moduleTypeInstanceId &&
          (a.customData as DocumentLinkCustomData)?.annotationType === 'documentLink',
      );
      if (annotation) {
        this.suppressedAnnotationDeleteIds.add(annotation.id);
        try {
          await instance.delete(annotation);
        } catch (error) {
          this.suppressedAnnotationDeleteIds.delete(annotation.id);
          throw error;
        }
        return;
      }
    }
  };

  placeIcon = () => {
    document.body.style.cursor = 'crosshair';
    this.showClickMessage.set(true);
    this.nutrientInstance?.addEventListener('page.press', this.onPagePress);
  };

  cancelPlaceIcon = () => {
    document.body.style.cursor = 'default';
    this.showClickMessage.set(false);
    this.nutrientInstance?.removeEventListener('page.press', this.onPagePress);
  };

  private onPagePress = (event: NutrientPagePressEvent) => {
    this.cancelPlaceIcon();
    this.ngZone.run(() => {
      const dialogRef = this.dialog.open<
        NutrientDocumentLinkDialogComponent,
        NutrientDocumentLinkDialogData,
        PdfMarkupAssociationDetail
      >(NutrientDocumentLinkDialogComponent, {
        panelClass: ['cipo-dialog', 'classic'],
        ...(this.userStore.isSmallScreen() ? DIALOG_SIZE.full : this.userService.getResponsiveDialogSize().md),
        data: {
          documentId: this.currentDocumentId(),
          entityInstanceId: this.data.entityInstanceId,
          annotationClick: event,
          createAnnotation: this.createAnnotation,
          deleteAnnotationOnly: this.deleteAnnotationOnlyFromViewer,
        },
      });
      dialogRef.afterClosed().subscribe(association => {
        if (association) this.associationDetails.set(association.moduleTypeInstanceId, association);
      });
    });
  };

  createAnnotation = (
    moduleTypeInstanceId: number,
    annotationClick: NutrientPagePressEvent,
    displayName: string,
  ): Promise<void> => {
    if (!this.nutrientInstance) return Promise.reject(new Error('Nutrient viewer is not loaded'));
    const instance = this.nutrientInstance;
    const customData: DocumentLinkCustomData = { annotationType: 'documentLink', moduleTypeInstanceId };
    const pageHeight =
      instance.pageInfoForIndex(annotationClick.pageIndex)?.height ?? DOCUMENT_LINK_REFERENCE_PAGE_HEIGHT;
    const pageScale = Math.max(1, pageHeight / DOCUMENT_LINK_REFERENCE_PAGE_HEIGHT);

    return this.createDocumentLinkImage(displayName, pageScale)
      .then(({ blob, width, height }) =>
        instance.createAttachment(blob).then(imageAttachmentId => {
          const annotation = new NutrientViewer.Annotations.ImageAnnotation({
            pageIndex: annotationClick.pageIndex,
            contentType: 'image/png',
            customData,
            imageAttachmentId,
            description: 'Document Association',
            boundingBox: new NutrientViewer.Geometry.Rect({
              left: annotationClick.point.x,
              top: annotationClick.point.y,
              width,
              height,
            }),
          });
          return instance.create(annotation);
        }),
      )
      .then(() => undefined);
  };

  /**
   * Document links are raster annotations, so their visual identifier must be embedded in the image.
   * The selected document's display name is available before its association is persisted.
   */
  private createDocumentLinkImage(
    displayName: string,
    pageScale: number,
  ): Promise<{ blob: Blob; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const icon = new Image();
      icon.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Unable to create document link image'));
          return;
        }

        const scale = window.devicePixelRatio || 2;
        const fontSize = 12;
        const iconSize = 16;
        const padding = 4;
        const gap = 4;
        context.font = `${fontSize}px Arial, sans-serif`;
        const width = padding + iconSize + gap + context.measureText(displayName).width + padding;
        const height = padding * 2 + Math.max(iconSize, fontSize + 2);

        canvas.width = Math.ceil(width * pageScale * scale);
        canvas.height = Math.ceil(height * pageScale * scale);
        context.scale(scale * pageScale, scale * pageScale);
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        context.strokeStyle = '#6a7aca';
        context.lineWidth = 0.5;
        context.strokeRect(0.5, 0.5, width - 1, height - 1);
        context.drawImage(icon, padding, (height - iconSize) / 2, iconSize, iconSize);
        context.font = `${fontSize}px Arial, sans-serif`;
        context.fillStyle = '#000000';
        context.textBaseline = 'middle';
        context.fillText(displayName, padding + iconSize + gap, height / 2 + 2);
        canvas.toBlob(blob => {
          if (blob) resolve({ blob, width: width * pageScale, height: height * pageScale });
          else reject(new Error('Unable to create document link image'));
        }, 'image/png');
      };
      icon.onerror = () => reject(new Error('Unable to load the CIPO document link icon'));
      icon.src = '/Content/pspdfkit/icons/cipo96x96.png';
    });
  }
}
